/**
 * Exam-subdomain catalog rules.
 * Exam hosts (lnat., tmua., …) show that host's catalogue subjects.
 * The edu portal, including pathbuddy.com, loads the full library. The cases
 * page filters it with subject bubbles, defaulting to the onboarding subject.
 */

const SUBJECT_ALIASES = {
  Law: ['law', 'legal', 'lnat'],
  'Investment Banking': ['investment banking', 'banking', 'finance', 'ib', 'financial'],
  'Computer Science': ['computer science', 'computing', 'cs', 'software', 'technology', 'math', 'mathematics'],
  Engineering: ['engineering'],
  Medicine: ['medicine', 'medical', 'health', 'ucat'],
  Geography: ['geography'],
  'Management Consulting': ['consulting', 'management consulting', 'strategy'],
};

export const EXAM_HOST_CATALOG = {
  lnat: {
    relatedSubjects: ['Law'],
  },
  tmua: {
    relatedSubjects: [
      'Computer Science',
      'Investment Banking',
      'Engineering',
    ],
  },
  ucat: {
    relatedSubjects: ['Medicine'],
  },
  esat: {
    relatedSubjects: ['Engineering', 'Computer Science'],
  },
  tara: {
    relatedSubjects: ['Computer Science', 'Investment Banking'],
  },
  tsa: {
    relatedSubjects: ['Management Consulting', 'Law', 'Investment Banking'],
  },
};

export function getExamHostKey(hostname) {
  const h = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();
  if (h.startsWith('lnat.') || h.includes('lnat.ivori') || h.includes('lnat.ivoir')) return 'lnat';
  if (h.startsWith('tmua.') || h.includes('tmua.ivori')) return 'tmua';
  if (h.startsWith('ucat.') || h.includes('ucat.ivori')) return 'ucat';
  if (h.startsWith('esat.') || h.includes('esat.ivori')) return 'esat';
  if (h.startsWith('tara.') || h.includes('tara.ivori')) return 'tara';
  if (h.startsWith('tsa.') || h.includes('tsa.ivori')) return 'tsa';
  return null;
}

export function isUnsetSubject(value) {
  const s = String(value || '').trim().toLowerCase();
  return !s || s === 'undecided' || s === 'none' || s === 'null';
}

/**
 * Map Settings / onboarding primary_subject → case-study subject label(s).
 * Empty array = undecided / unset → show the full case library.
 */
export function resolvePrimarySubjectCaseSubjects(userSubject) {
  if (isUnsetSubject(userSubject)) return [];
  const needle = String(userSubject).trim().toLowerCase();

  for (const [canonical, aliases] of Object.entries(SUBJECT_ALIASES)) {
    if (canonical.toLowerCase() === needle) return [canonical];
    if ((aliases || []).some((a) => String(a).toLowerCase() === needle)) {
      return [canonical];
    }
  }

  // Exam-host slugs stored as primary_subject (e.g. lnat → Law)
  const catalog = EXAM_HOST_CATALOG[needle];
  if (catalog?.relatedSubjects?.length === 1) {
    return catalog.relatedSubjects;
  }
  if (catalog?.relatedSubjects?.length) {
    return [...catalog.relatedSubjects];
  }

  return [String(userSubject).trim()];
}

/**
 * Free: has not paid, and not a member of an organisation.
 * `access` is the account model on every auth payload, and `access.has_paid`
 * is the server's answer to "has this account paid" (a Beginnings choice is
 * not a payment). `active_plan` is no longer authoritative: an account can
 * hold an exam while it still says `free`. A session from before `has_paid`
 * existed falls back to the plan name, and one from before `access` existed
 * to active_plan and the org-admin flag.
 */
export function freePlanOutsideOrganisation(user) {
  if (!user) return false;
  const access = user.access;
  if (access && typeof access === 'object') {
    if (access.org) return false;
    if (typeof access.has_paid === 'boolean') return access.has_paid === false;
    return String(access.plan || 'free').trim().toLowerCase() === 'free';
  }
  const plan = String(user.active_plan || 'free').trim().toLowerCase();
  return plan === 'free' && !user.is_org_admin;
}

/**
 * The exam the account owns first (`access.owned.exams[0]`), upper-cased
 * ("LNAT"), or null when it owns none or the payload has no owned list.
 */
export function firstOwnedExamName(user) {
  const exams = user?.access?.owned?.exams;
  if (!Array.isArray(exams)) return null;
  const first = exams.find((entry) => entry && typeof entry.exam === 'string' && entry.exam.trim());
  return first ? first.exam.trim().toUpperCase() : null;
}

const DEFAULT_EXAM_CANDIDATES = ['lnat', 'tmua', 'ucat', 'esat', 'tara'];

/**
 * The exam the quiz generator opens on when the route names none. An account
 * that owns exams lands on one it owns: the exam the host names, then the
 * onboarding subject names, when owned (`all_tracks` and org seats own all
 * five), otherwise the first owned exam. Without an owned exam, the host or
 * the onboarding subject names an exam; only a session from before the owned
 * list existed still reads the plan name. Returns "LNAT" etc., or null for
 * the hero.
 */
export function defaultExamForUser(user, { userSubject = '', origin = '' } = {}) {
  const subject = String(userSubject || '').toLowerCase().trim();
  const host = String(origin || '').toLowerCase();
  const first = firstOwnedExamName(user);
  if (first) {
    const owned = new Set(
      user.access.owned.exams
        .filter((entry) => entry && typeof entry.exam === 'string')
        .map((entry) => entry.exam.trim().toLowerCase()),
    );
    const named = DEFAULT_EXAM_CANDIDATES.find((ex) => owned.has(ex) && host.includes(ex))
      || DEFAULT_EXAM_CANDIDATES.find((ex) => owned.has(ex) && (subject === ex || subject.includes(ex)));
    return named ? named.toUpperCase() : first;
  }
  const plan = hasOwnedExamList(user) ? '' : String(user?.active_plan || '').toLowerCase().trim();
  const match = DEFAULT_EXAM_CANDIDATES.find(
    (ex) => host.includes(ex) || plan.includes(ex) || subject === ex || subject.includes(ex),
  );
  return match ? match.toUpperCase() : null;
}

/** True once the server has sent the owned list (an array, possibly empty). */
export function hasOwnedExamList(user) {
  return Array.isArray(user?.access?.owned?.exams);
}

/**
 * True for an org admin, or a free plan outside an organisation.
 * The cases page no longer uses this to hide the library.
 */
export function unmappedCaseLibraryForUser(user) {
  if (!user) return false;
  return Boolean(user.is_org_admin) || freePlanOutsideOrganisation(user);
}

/** True when primary_subject is a catalogue subject (Law, Medicine, …). */
export function primarySubjectMapsToCaseCatalogue(userSubject) {
  if (isUnsetSubject(userSubject)) return false;
  const needle = String(userSubject).trim().toLowerCase();
  for (const [canonical, aliases] of Object.entries(SUBJECT_ALIASES)) {
    if (canonical.toLowerCase() === needle) return true;
    if ((aliases || []).some((alias) => String(alias).toLowerCase() === needle)) return true;
  }
  return Boolean(EXAM_HOST_CATALOG[needle]);
}

/**
 * Subjects to fetch on this host.
 * Empty array = the full library. Exam hosts return their catalogue subjects.
 * The onboarding subject does not narrow this list; the cases page uses it
 * only to choose the default subject bubble.
 */
export function resolveExamHostCaseSubjects(_userSubject, hostname) {
  const hostKey = getExamHostKey(hostname);
  const catalog = hostKey ? EXAM_HOST_CATALOG[hostKey] : null;
  if (!catalog) return [];
  return [...(catalog.relatedSubjects || [])];
}

function aliasNeedles(subject) {
  const raw = String(subject || '').trim();
  if (!raw) return [];
  const extras = SUBJECT_ALIASES[raw] || [];
  return [...new Set([raw.toLowerCase(), ...extras.map((a) => String(a).toLowerCase())])];
}

function subjectFieldMatches(caseSubject, needle) {
  const field = String(caseSubject || '').trim().toLowerCase();
  const n = String(needle || '').trim().toLowerCase();
  if (!field || !n) return false;
  if (field === n) return true;
  const escaped = n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i').test(field);
}

/** Match the case `subject` column only — never title or industry sub-rows. */
export function caseMatchesSubject(caseItem, subject) {
  if (!subject) return true;
  const caseSubject = caseItem?.subject;
  if (!caseSubject) return false;
  return aliasNeedles(subject).some((needle) => subjectFieldMatches(caseSubject, needle));
}

export function filterCasesForExamHost(cases, { hostname } = {}) {
  const list = Array.isArray(cases) ? cases : [];
  const hostKey = getExamHostKey(hostname);
  const allowedSubjects = hostKey && EXAM_HOST_CATALOG[hostKey]
    ? EXAM_HOST_CATALOG[hostKey].relatedSubjects
    : null;

  if (!allowedSubjects || allowedSubjects.length === 0) return list;
  return list.filter((c) => allowedSubjects.some((subj) => caseMatchesSubject(c, subj)));
}
